// 统计学学习主题数据结构 - 基于 Important Topics.xlsx
// 按照正确的层级结构组织

export interface TopicItem {
  code: string;
  name: string;
  chinese_name: string;
}

export interface SubCategory {
  code: string;
  name: string;
  chinese_name: string;
  topics_count: number;
  topics: TopicItem[];
}

export interface MainCategory {
  code: string;
  name: string;
  chinese_name: string;
  topics_count: number;
  icon: string;
  color: string;
  subcategories: SubCategory[];
}

// 扁平化的主题接口
export interface FlatTopic {
  id: string;
  code: string;
  title: string;
  chineseTitle: string;
  description: string;
  mainCategoryCode: string;  // 所属大类代码
  mainCategoryName: string;  // 所属大类名称
  subCategoryCode: string;   // 所属子类代码
  subCategoryName: string;   // 所属子类名称
}

// 完整的层级结构数据
export const TOPICS_HIERARCHY: MainCategory[] = [
  {
    code: 'DS',
    name: 'Descriptive Statistics',
    chinese_name: '描述统计',
    topics_count: 22,
    icon: '📊',
    color: '#3498db',
    subcategories: [
      {
        code: 'GD',
        name: 'Graphical Displays',
        chinese_name: '数据可视化',
        topics_count: 11,
        topics: [
          { code: 'GD1', name: 'Interpreting a pie chart', chinese_name: '解读饼图' },
          { code: 'GD2', name: 'Computations from pie charts', chinese_name: '基于饼图的计算' },
          { code: 'GD3', name: 'Interpreting a double bar graph', chinese_name: '解读双条形图' },
          { code: 'GD4', name: 'Histograms for grouped data', chinese_name: '分组数据的直方图' },
          { code: 'GD5', name: 'Frequency polygons for grouped data', chinese_name: '分组数据的频数多边形' },
          { code: 'GD6', name: 'Interpreting relative frequency histograms', chinese_name: '解读相对频数直方图' },
          { code: 'GD7', name: 'Cumulative distributions and ogives', chinese_name: '累积分布和累积频率曲线' },
          { code: 'GD8', name: 'Comparing means without calculation', chinese_name: '无需计算比较均值' },
          { code: 'GD9', name: 'Comparing standard deviations without calculation', chinese_name: '无需计算比较标准差' },
          { code: 'GD10', name: 'Box-and-whisker plots', chinese_name: '箱线图(盒须图)' },
          { code: 'GD11', name: 'Interpreting a stem-and-leaf display', chinese_name: '解读茎叶图' }
        ]
      },
      {
        code: 'DM',
        name: 'Descriptive Measures',
        chinese_name: '描述性度量',
        topics_count: 11,
        topics: [
          { code: 'DM1', name: 'Mean, median, and mode: Computations', chinese_name: '计算均值、中位数和众数' },
          { code: 'DM2', name: 'Rejecting unreasonable claims based on average statistics', chinese_name: '基于平均统计量拒绝不合理的断言' },
          { code: 'DM3', name: 'Weighted mean: Tabular data', chinese_name: '加权均值' },
          { code: 'DM4', name: 'Approximating the mean of a data set given a histogram', chinese_name: '给定直方图估算数据集的均值' },
          { code: 'DM5', name: 'Percentiles', chinese_name: '百分位数' },
          { code: 'DM6', name: 'Population standard deviation', chinese_name: '总体标准差' },
          { code: 'DM7', name: 'Sample standard deviation', chinese_name: '样本标准差' },
          { code: 'DM8', name: 'Approximating the standard deviation of a data set given a histogram', chinese_name: '给定直方图估算数据集的标准差' },
          { code: 'DM9', name: 'Chebyshev\'s theorem and the empirical rule', chinese_name: '切比雪夫定理和经验法则' },
          { code: 'DM10', name: 'Mean, median, and mode: Comparisons', chinese_name: '均值、中位数和众数：比较' },
          { code: 'DM11', name: 'Making reasonable inferences based on proportion statistics', chinese_name: '基于比例统计量做出合理的推断' }
        ]
      }
    ]
  },
  {
    code: 'P',
    name: 'Probability',
    chinese_name: '概率',
    topics_count: 16,
    icon: '🎲',
    color: '#f39c12',
    subcategories: [
      {
        code: 'C',
        name: 'Counting',
        chinese_name: '计数',
        topics_count: 3,
        topics: [
          { code: 'C1', name: 'Factorial expressions', chinese_name: '阶乘表达式' },
          { code: 'C2', name: 'Combinations', chinese_name: '组合' },
          { code: 'C3', name: 'Probabilities of draws with replacement', chinese_name: '有放回抽取的概率' }
        ]
      },
      {
        code: 'EP',
        name: 'Events and Probability',
        chinese_name: '事件与概率',
        topics_count: 6,
        topics: [
          { code: 'EP1', name: 'Outcomes and event probability', chinese_name: '结果与事件概率' },
          { code: 'EP2', name: 'Probabilities involving two dice: Decimal answers', chinese_name: '涉及两个骰子的概率：小数答案' },
          { code: 'EP3', name: 'Word problem involving the probability of a union or an intersection', chinese_name: '涉及并集或交集概率的应用题' },
          { code: 'EP4', name: 'Probability of the union and intersection of independent events', chinese_name: '独立事件的并集和交集概率' },
          { code: 'EP5', name: 'Probability of the union of mutually exclusive events and independent events', chinese_name: '互斥事件和独立事件的并集概率' },
          { code: 'EP6', name: 'Probability of independent events: Decimal answers', chinese_name: '独立事件的概率：小数答案' }
        ]
      },
      {
        code: 'CP',
        name: 'Conditional Probability',
        chinese_name: '条件概率',
        topics_count: 7,
        topics: [
          { code: 'CP1', name: 'Calculating relative frequencies in a contingency table', chinese_name: '计算列联表中的相对频数' },
          { code: 'CP2', name: 'Conditional probability: Basic', chinese_name: '条件概率基础' },
          { code: 'CP3', name: 'Probability of dependent events', chinese_name: '相关事件(非独立事件)的概率' },
          { code: 'CP4', name: 'Intersection and conditional probability', chinese_name: '交集与条件概率' },
          { code: 'CP5', name: 'Tree diagrams for conditional probabilities', chinese_name: '条件概率的树状图' },
          { code: 'CP6', name: 'Law of total probabilities', chinese_name: '全概率公式' },
          { code: 'CP7', name: 'Bayes\' theorem', chinese_name: '贝叶斯定理' }
        ]
      }
    ]
  },
  {
    code: 'RVD',
    name: 'Random Variables and Distributions',
    chinese_name: '随机变量与分布',
    topics_count: 20,
    icon: '📈',
    color: '#9b59b6',
    subcategories: [
      {
        code: 'ORV',
        name: 'One Random Variable',
        chinese_name: '单随机变量',
        topics_count: 5,
        topics: [
          { code: 'ORV1', name: 'Classification of variables and levels of measurement', chinese_name: '变量分类与测量水平' },
          { code: 'ORV2', name: 'Discrete versus continuous variables', chinese_name: '离散变量与连续变量' },
          { code: 'ORV3', name: 'Discrete probability distribution: Basic', chinese_name: '离散概率分布：基础' },
          { code: 'ORV4', name: 'Discrete probability distribution: Word problems', chinese_name: '离散概率分布：应用题' },
          { code: 'ORV5', name: 'Expectation and variance of a random variable', chinese_name: '随机变量的期望与方差' }
        ]
      },
      {
        code: 'FD',
        name: 'Fundamental Distributions',
        chinese_name: '基本分布',
        topics_count: 13,
        topics: [
          { code: 'FD6', name: 'Binomial problems: Mean and standard deviation', chinese_name: '二项分布：均值与标准差' },
          { code: 'FD7', name: 'Binomial problems: Basic', chinese_name: '二项分布：基础' },
          { code: 'FD8', name: 'Binomial problems: Advanced', chinese_name: '二项分布：进阶' },
          { code: 'FD9', name: 'Standard normal probabilities', chinese_name: '标准正态概率' },
          { code: 'FD10', name: 'Standard normal values: Basic', chinese_name: '标准正态值(Z值)：基础' },
          { code: 'FD11', name: 'Standard normal values: Advanced', chinese_name: '标准正态值(Z值)：进阶' },
          { code: 'FD12', name: 'Normal versus standard normal density curves', chinese_name: '正态密度曲线与标准正态密度曲线' },
          { code: 'FD13', name: 'Normal distribution: Finding a raw score', chinese_name: '正态分布：查找原始分数' },
          { code: 'FD14', name: 'Normal distribution: Finding a probability, basic', chinese_name: '正态分布：查找概率，基础' },
          { code: 'FD15', name: 't distribution', chinese_name: 't 分布' },
          { code: 'FD16', name: 'Chi-square distribution', chinese_name: '卡方分布(χ2分布)' },
          { code: 'FD17', name: 'F distribution', chinese_name: 'F 分布' },
          { code: 'FD18', name: 'Normal approximation to binomial', chinese_name: '二项分布的正态近似' }
        ]
      },
      {
        code: 'CLT',
        name: 'Central Limit Theorem',
        chinese_name: '中心极限定理',
        topics_count: 2,
        topics: [
          { code: 'CLT1', name: 'Central limit theorem: Sample mean', chinese_name: '中心极限定理：样本均值' },
          { code: 'CLT2', name: 'Central limit theorem: Sample proportion', chinese_name: '中心极限定理：样本比例' }
        ]
      }
    ]
  },
  {
    code: 'CIHT',
    name: 'Confidence Intervals and Hypothesis Testing',
    chinese_name: '置信区间与假设检验',
    topics_count: 23,
    icon: '🎯',
    color: '#2ecc71',
    subcategories: [
      {
        code: 'CI',
        name: 'Confidence Intervals',
        chinese_name: '置信区间',
        topics_count: 9,
        topics: [
          { code: 'CI1', name: 'Selecting a distribution for inferences on the population mean', chinese_name: '选择用于总体均值推断的分布' },
          { code: 'CI2', name: 'Confidence interval for the population mean: Use of the standard normal', chinese_name: '总体均值的置信区间：使用标准正态分布' },
          { code: 'CI3', name: 'Confidence interval for the population mean: Use of the t distribution', chinese_name: '总体均值的置信区间：使用 t 分布' },
          { code: 'CI4', name: 'Confidence interval for a population proportion', chinese_name: '总体比例的置信区间' },
          { code: 'CI5', name: 'Confidence interval for the population standard deviation', chinese_name: '总体标准差的置信区间' },
          { code: 'CI6', name: 'Confidence interval for the difference of population means: Use of the standard normal', chinese_name: '总体均值差的置信区间：使用标准正态分布' },
          { code: 'CI7', name: 'Confidence interval for the difference of population means: Use of the t distribution', chinese_name: '总体均值差的置信区间：使用 t 分布' },
          { code: 'CI8', name: 'Confidence interval for the difference of population proportions', chinese_name: '总体比例差的置信区间' },
          { code: 'CI9', name: 'Confidence interval for the ratio of population variances', chinese_name: '总体方差比的置信区间' }
        ]
      },
      {
        code: 'SEP',
        name: 'Sample Size, Effect Size, and Power',
        chinese_name: '样本量、效应量与统计效力',
        topics_count: 4,
        topics: [
          { code: 'SEP1', name: 'Choosing an appropriate sample size', chinese_name: '选择合适的样本量' },
          { code: 'SEP2', name: 'Type I and Type II errors', chinese_name: '第一类错误和第二类错误' },
          { code: 'SEP3', name: 'Type I and Type II errors and power', chinese_name: '第一类错误、第二类错误与功效' },
          { code: 'SEP4', name: 'Effect size, sample size, and power', chinese_name: '效应量、样本量与功效' }
        ]
      },
      {
        code: 'HT',
        name: 'Hypothesis Tests',
        chinese_name: '假设检验',
        topics_count: 10,
        topics: [
          { code: 'HT1', name: 'Determining null and alternative hypotheses', chinese_name: '确定零假设和备择假设' },
          { code: 'HT2', name: 'Hypothesis test for the population mean: Z test', chinese_name: '总体均值的假设检验：Z 检验' },
          { code: 'HT3', name: 'Hypothesis test for the population mean: t test', chinese_name: '总体均值的假设检验：t 检验' },
          { code: 'HT4', name: 'Hypothesis test for a population proportion', chinese_name: '总体比例的假设检验' },
          { code: 'HT5', name: 'Hypothesis test for the population variance or standard deviation', chinese_name: '总体方差或标准差的假设检验' },
          { code: 'HT6', name: 'Hypothesis test for the difference of population means: Z test', chinese_name: '总体均值差的假设检验：Z 检验' },
          { code: 'HT7', name: 'Hypothesis test for the difference of population means: Paired comparisons', chinese_name: '总体均值差的假设检验：配对比较' },
          { code: 'HT8', name: 'Hypothesis test for the difference of population means: t test', chinese_name: '总体均值差的假设检验：t 检验' },
          { code: 'HT9', name: 'Hypothesis test for the difference of population proportions', chinese_name: '总体比例差的假设检验' },
          { code: 'HT10', name: 'Hypothesis test for the ratio of population variances', chinese_name: '总体方差比的假设检验' }
        ]
      }
    ]
  },
  {
    code: 'RC',
    name: 'Regression and Correlation',
    chinese_name: '回归与相关',
    topics_count: 7,
    icon: '📉',
    color: '#e91e63',
    subcategories: [
      {
        code: 'CSR',
        name: 'Correlation and Simple Linear Regression',
        chinese_name: '相关与简单线性回归',
        topics_count: 5,
        topics: [
          { code: 'CSR1', name: 'Sketching the least-squares regression line', chinese_name: '绘制最小二乘回归线' },
          { code: 'CSR2', name: 'Linear relationship and the sample correlation coefficient', chinese_name: '线性关系与样本相关系数' },
          { code: 'CSR3', name: 'Predictions from the least-squares regression line', chinese_name: '基于最小二乘回归线的预测' },
          { code: 'CSR4', name: 'Computing the sample correlation coefficient and the coefficients for the least-squares regression line', chinese_name: '计算样本相关系数及最小二乘回归线系数' },
          { code: 'CSR5', name: 'Explained and unexplained variation and the least-squares regression line', chinese_name: '解释变异与未解释变异及最小二乘回归线' }
        ]
      },
      {
        code: 'ISLR',
        name: 'Inference from Simple Linear Regression',
        chinese_name: '简单线性回归的推断',
        topics_count: 2,
        topics: [
          { code: 'ISLR1', name: 'Confidence intervals and prediction intervals from simple linear regression', chinese_name: '简单线性回归的置信区间与预测区间' },
          { code: 'ISLR2', name: 'Hypothesis tests for the correlation coefficient and the slope of the least-squares regression line', chinese_name: '相关系数与最小二乘回归线斜率的假设检验' }
        ]
      }
    ]
  },
  {
    code: 'ACNT',
    name: 'ANOVA, Chi-square and Nonparametric Tests',
    chinese_name: '方差分析、卡方检验与非参数检验',
    topics_count: 12,
    icon: '📊',
    color: '#1abc9c',
    subcategories: [
      {
        code: 'OIA',
        name: 'One-way, Independent-samples ANOVA',
        chinese_name: '单因素独立样本方差分析',
        topics_count: 3,
        topics: [
          { code: 'OIA1', name: 'ANOVA: Mean squares and the common population variance', chinese_name: '方差分析(ANOVA)：均方与共同总体方差' },
          { code: 'OIA2', name: 'ANOVA: Degrees of freedom and the F statistic', chinese_name: '方差分析(ANOVA)：自由度与 F 统计量' },
          { code: 'OIA3', name: 'ANOVA: Hypothesis tests and the ANOVA table', chinese_name: '方差分析(ANOVA)：假设检验与方差分析表' }
        ]
      },
      {
        code: 'AA',
        name: 'Advanced ANOVA',
        chinese_name: '进阶方差分析',
        topics_count: 4,
        topics: [
          { code: 'AA1', name: 'One-way, repeated-measures ANOVA', chinese_name: '单因素重复测量方差分析' },
          { code: 'AA2', name: 'Interpreting group means from a factorial design', chinese_name: '解读析因设计中的组均值' },
          { code: 'AA3', name: 'Two-way, independent-samples ANOVA', chinese_name: '双因素独立样本方差分析' },
          { code: 'AA4', name: 'Selecting among t tests and ANOVA tests', chinese_name: '在 t 检验与方差分析之间进行选择' }
        ]
      },
      {
        code: 'CST',
        name: 'Chi-square Tests',
        chinese_name: '卡方检验',
        topics_count: 3,
        topics: [
          { code: 'CST1', name: 'Contingency tables: Expected frequencies', chinese_name: '列联表：期望频数' },
          { code: 'CST2', name: 'Chi-square goodness-of-fit test', chinese_name: '卡方拟合优度检验' },
          { code: 'CST3', name: 'Chi-square test of independence', chinese_name: '卡方独立性检验' }
        ]
      },
      {
        code: 'NPT',
        name: 'Non-parametric Tests',
        chinese_name: '非参数检验',
        topics_count: 2,
        topics: [
          { code: 'NPT1', name: 'Sign test', chinese_name: '符号检验' },
          { code: 'NPT2', name: 'Wilcoxon signed-ranks test', chinese_name: '威尔科克森符号秩检验' }
        ]
      }
    ]
  }
];

// 生成扁平化主题列表的函数
export function generateFlatTopics(): FlatTopic[] {
  const flatTopics: FlatTopic[] = [];
  let index = 1;

  for (const mainCategory of TOPICS_HIERARCHY) {
    for (const subCategory of mainCategory.subcategories) {
      for (const topic of subCategory.topics) {
        flatTopics.push({
          id: `topic-${index++}`,
          code: topic.code,
          title: topic.name,
          chineseTitle: topic.chinese_name,
          description: `学习${topic.chinese_name}的核心概念和应用`,
          mainCategoryCode: mainCategory.code,
          mainCategoryName: mainCategory.chinese_name,
          subCategoryCode: subCategory.code,
          subCategoryName: subCategory.chinese_name
        });
      }
    }
  }

  return flatTopics;
}

export const FLAT_TOPICS = generateFlatTopics();

// 向后兼容的导出（保持与旧代码的兼容性）
export interface ExcelTopic {
  code: string;
  name: string;
  chinese_name: string;
  topics_count?: number | null;
}

export interface ExcelCategory {
  code: string;
  name: string;
  chinese_name: string;
  color: string;
  icon: string;
}

// 向后兼容：生成旧格式的 EXCEL_TOPICS
export const EXCEL_TOPICS: ExcelTopic[] = FLAT_TOPICS.map(topic => ({
  code: topic.code,
  name: topic.title,
  chinese_name: topic.chineseTitle
}));
